import { useCallback, useEffect, useRef, useState } from 'react';
import { codePageGate, offerLive, STAGE } from '../../../services/exchange/codePageGate';
import {
  leaveOffer,
  tapOffer,
  watchOffer,
  isSettled,
} from '../../../services/exchange/exchangeOffer';
import { commitHeldChoice } from '../../../services/exchange/heldChoice';
import { findExistingConnection } from '../../../services/activeSession';
import { logExchangeOfferEnded } from '../../../services/analytics/eventLogger';
import { busyTitle, firstNameOf } from '../../../utils/contactUtils';

// How long the busy line stays on the sheet before the code comes back (P4)
export const BUSY_LINE_MS = 2500;

/**
 * The code page's side of two profile holders (E3b-2a steps 6–7): the server
 * offer behind the gate. Nothing is recorded until both have tapped.
 *
 * - **An offer** (a scan — this phone's, or someone's of this code; or the
 *   scan a nearby match runs underneath) goes to the gate; one it can't take
 *   (another person's sheet is up) is left at once.
 * - **Someone nearby** (useProximity's first person) raises the sheet on the
 *   bare code; their going quietly takes it away (D10).
 * - **A tap holds the choice.** With a room: tap the offer. Nearby, before a
 *   match: send the request; the match's offer then lands on the waiting stage
 *   and is tapped with the choice already held.
 * - **While an offer is live** it is polled (~1 s, the "still here"): `formed`
 *   → this phone's writes (offer.form), then the held share, then People;
 *   `over` → the bare code (the host opens a fresh room).
 * - **Nearby cancels**: my request gone on the server without a match (R1),
 *   `busy` (R2/P4 — the busy line, then the code), or the person gone.
 * - **The waiting X** leaves the offer, or withdraws the nearby request (F5).
 *
 * @param {Object} p
 * @param {Object} p.gate - codePageGate state
 * @param {Function} p.dispatch
 * @param {Object|null} p.offer - pxpOffer's offer, a new object per offer
 * @param {Object|null} p.offerFailed - { reason, name } the scan behind a nearby
 *   match couldn't claim (P4), a new object each time
 * @param {Object} [p.nearby] - { user, waitingOn, matched, invite(peerId), withdraw(peerId) }
 * @param {string|null} p.me - own profile id
 * @param {Object|null} p.profile - own profile (the held share)
 * @param {Function} p.loadConnectionData - context: a room's record, read fresh
 * @param {Function} p.updateContactSharingStatus - context
 * @param {Function} [p.onRearm] - the host's code is free again: a fresh room
 * @returns {{ waitingLabel: string, leave: Function }}
 */
const useCodePageOffer = ({
  gate,
  dispatch,
  offer,
  offerFailed,
  nearby,
  me,
  profile,
  loadConnectionData,
  updateContactSharingStatus,
  onRearm,
}) => {
  const gateRef = useRef(gate);
  gateRef.current = gate;
  const offerRef = useRef(null); // the offer the gate took
  const answerRef = useRef(null); // the server's formed answer
  const [busyName, setBusyName] = useState(null);
  // A nearby person whose offer just ended (busy, over, the X, gone): their
  // sheet doesn't rise again until they leave the nearby list
  const spentPeer = useRef(null);
  const spend = () => {
    const g = gateRef.current;
    if (g.via === 'nearby') spentPeer.current = g.peer?.profileUserId || null;
  };

  // Every end of an offer: logged with why, the person spent, the host's code freed
  const finish = (reason) => {
    const g = gateRef.current;
    const o = offerRef.current;
    logExchangeOfferEnded(g.room, { reason, role: o?.role || null, via: g.via, stage: g.stage });
    spend();
    offerRef.current = null;
    if (o?.role === 'host') onRearm?.();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  /** The server ended it (or the page's nearby request did): back to the code. */
  const ended = useCallback(
    (reason) => {
      finishRef.current(reason);
      dispatch({ type: 'over' });
    },
    [dispatch]
  );

  /** A server answer that settles the offer: formed → this phone's writes; over → the code. */
  const settle = useCallback(
    (answer) => {
      if (answer.state === 'formed') {
        answerRef.current = answer;
        dispatch({ type: 'formed' });
      } else if (answer.state === 'over') ended(answer.reason || 'over');
    },
    [dispatch, ended]
  );

  // ── An offer arrives ──
  useEffect(() => {
    if (!offer) return;
    const ev = {
      type: 'offer',
      room: offer.room,
      peer: offer.peer,
      repeat: offer.repeat,
      via: offer.via,
      existingRoom: offer.existingRoom || null,
    };
    if (codePageGate(gateRef.current, ev) === gateRef.current) {
      // Another person's sheet is up: one person at a time
      leaveOffer(offer.room, offer.me);
      return;
    }
    offerRef.current = offer;
    answerRef.current = null;
    dispatch(ev);
  }, [offer, dispatch]);

  // ── Someone nearby: raise the sheet on the bare code (2a: the first person) ──
  const nearbyUser = nearby?.user || null;
  useEffect(() => {
    if (spentPeer.current && nearbyUser?.profileUserId !== spentPeer.current) {
      spentPeer.current = null;
    }
  }, [nearbyUser?.profileUserId]);
  useEffect(() => {
    if (!nearbyUser || !me || gate.stage !== STAGE.REST) return undefined;
    if (nearbyUser.profileUserId === spentPeer.current) return undefined;
    let live = true;
    const peer = {
      profileUserId: nearbyUser.profileUserId,
      name: nearbyUser.name || '',
      photoUrl: nearbyUser.photoUrl || null,
    };
    // First time or a repeat — read-only, before the sheet rises
    findExistingConnection(me, peer.profileUserId)
      .catch(() => null)
      .then((existing) => {
        if (live) dispatch({ type: 'nearby', peer, repeat: !!existing, existingRoom: existing });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyUser?.profileUserId, me, gate.stage === STAGE.REST]);

  // ── The nearby person stopped looking (before any claim): quietly away ──
  const peerId = gate.peer?.profileUserId;
  useEffect(() => {
    if (gate.via !== 'nearby' || gate.room || !peerId) return;
    if (nearbyUser?.profileUserId === peerId) return;
    if (gate.stage === STAGE.WAITING_PEER && nearby?.waitingOn === peerId) return;
    logExchangeOfferEnded(null, { reason: 'gone', via: 'nearby', stage: gate.stage });
    spentPeer.current = null; // gone from the list: a return is a fresh find
    dispatch({ type: 'gone', peerId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyUser?.profileUserId, peerId, gate.via, gate.room]);

  // ── A held choice meets a room: tap the offer ──
  const tapped = useRef(null);
  useEffect(() => {
    if (gate.stage !== STAGE.WAITING_PEER || !gate.room || !offerRef.current) return;
    if (tapped.current === gate.room) return;
    tapped.current = gate.room;
    const o = offerRef.current;
    tapOffer(o.room, o.me, o.tapArgs).then((answer) => {
      if (answer) settle(answer); // no answer: the poll asks again
    });
  }, [gate.stage, gate.room, settle]);

  // ── Nearby, before a match: a held choice sends the request ──
  const sent = useRef(false);
  const inviting = useRef(null); // the request in flight: the X waits for it (12.9)
  useEffect(() => {
    if (gate.stage !== STAGE.WAITING_PEER || gate.room || gate.via !== 'nearby') {
      sent.current = false;
      return;
    }
    const target = gate.peer?.profileUserId;
    const request = Promise.resolve(nearby?.invite(target));
    inviting.current = request;
    request.then((answer) => {
      if (inviting.current === request) inviting.current = null;
      if (gateRef.current.stage !== STAGE.WAITING_PEER || gateRef.current.room) return;
      if (answer === 'sent') sent.current = true;
      else if (answer === 'busy') setBusyName(gateRef.current.peer?.name || '');
      else if (answer !== 'matched') ended(answer || 'failed'); // not_nearby / no answer
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.stage, gate.room, gate.via]);

  // ── My request ended on the server without a match (R1: they took someone
  // else, their code was scanned, they left) ──
  useEffect(() => {
    if (!sent.current || gate.room || gate.stage !== STAGE.WAITING_PEER) return;
    if (nearby?.waitingOn || nearby?.matched) return;
    // A match clears it too, a beat before the match is heard: wait a moment
    const t = setTimeout(() => {
      if (gateRef.current.stage === STAGE.WAITING_PEER && !gateRef.current.room) ended('cancelled');
    }, 3000);
    return () => clearTimeout(t);
  }, [nearby?.waitingOn, nearby?.matched, gate.room, gate.stage, ended]);

  // ── The scan behind a match couldn't claim (P4): the busy line, then the code ──
  useEffect(() => {
    if (!offerFailed) return;
    const g = gateRef.current;
    if (g.stage !== STAGE.WAITING_PEER || g.via !== 'nearby' || g.room) return;
    if (offerFailed.reason === 'busy') setBusyName(g.peer?.name || offerFailed.name || '');
    else ended(offerFailed.reason || 'failed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerFailed]);
  useEffect(() => {
    if (busyName === null) return undefined;
    const t = setTimeout(() => {
      setBusyName(null);
      ended('busy');
    }, BUSY_LINE_MS);
    return () => clearTimeout(t);
  }, [busyName, ended]);

  // ── While an offer is live: poll it ──
  const live = offerLive(gate);
  useEffect(() => {
    if (!live || !offerRef.current) return undefined;
    const o = offerRef.current;
    return watchOffer(o.room, o.me, (answer) => {
      if (isSettled(answer)) settle(answer);
    });
  }, [live, gate.room, settle]);

  // ── Formed: this phone's writes, then the held share, then People ──
  const forming = useRef(false);
  useEffect(() => {
    if (gate.stage !== STAGE.FORMING || forming.current) return;
    forming.current = true;
    const o = offerRef.current;
    const choice = gate.choice;
    (async () => {
      let room = o?.room || null;
      try {
        room = await o.form(answerRef.current);
        const connectionData = await loadConnectionData(room, true);
        await commitHeldChoice({
          choice,
          room,
          profile,
          connectionData,
          updateContactSharingStatus,
        });
      } catch (err) {
        // Recorded on the server already (both tapped): land anyway — People's
        // recovery fills in what this phone missed (step 9)
        console.error('[CODE_PAGE] formed, but this side failed:', err);
      } finally {
        forming.current = false;
        offerRef.current = null;
        dispatch({ type: 'landed', room });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.stage]);

  /** The waiting X (F5) or the caret from the strip: nothing is recorded. */
  const withdraw = nearby?.withdraw;
  const leave = useCallback(
    (reason = 'dismissed') => {
      const g = gateRef.current;
      const o = offerRef.current;
      if (o && g.room) leaveOffer(o.room, o.me);
      else if (g.via === 'nearby' && g.stage === STAGE.WAITING_PEER) {
        // after the request's own answer — an X sent first would find nothing
        // to take back, and the request would land after it (12.9)
        const peerId = g.peer?.profileUserId;
        Promise.resolve(inviting.current).then(() => withdraw?.(peerId));
      }
      finishRef.current(reason);
    },
    [withdraw]
  );

  const first = firstNameOf(gate.peer?.name) || '';
  const waitingLabel =
    busyName !== null
      ? busyTitle(busyName)
      : first
        ? `Waiting for ${first}`
        : 'Waiting for connection';

  return { waitingLabel, leave };
};

export default useCodePageOffer;
